<template>
  <div>
    <v-img max-width="180" class="mb-8" src="@/assets/images/logo_roxo.png" />

    <template v-if="loading">
      <v-skeleton-loader type="article" />
    </template>

    <template v-if="!loading && linkInvalid">
      <h2>Ops! Este link não é válido.</h2>
      <p class="mt-4">
        Ele pode ter sido desativado. Entre em contato com o profissional que
        te enviou este link para pedir um novo.
      </p>
    </template>

    <template v-if="!loading && !linkInvalid && sent">
      <h2>Cadastro enviado com sucesso!</h2>
      <p class="mt-4">
        Seus dados foram enviados para {{ professional.name }}. Assim que o
        cadastro for aprovado, você será avisado(a).
      </p>
    </template>

    <template v-if="!loading && !linkInvalid && !sent">
      <h2>Cadastro</h2>
      <p class="mt-2">
        Preencha seus dados para se cadastrar com
        <strong>{{ professional.name }}</strong>
      </p>

      <v-form ref="form" class="mt-6" v-model="valid" lazy-validation>
        <h3 class="font-weight-regular mt-10 mb-4">
          <v-icon left>mdi-account-outline</v-icon>
          Dados pessoais
        </h3>
        <v-row>
          <v-col cols="12">
            <app-text-field v-model="form.name" label="Nome completo" :rules="requiredRules" outlined dense
              hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-whatsapp-field v-model="form.phone_wa" label="WhatsApp" :rules="requiredRules" outlined dense
              hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.email" label="E-mail" type="email" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-cpf-field v-model="form.cpf" label="CPF" :rules="requiredRules" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.rg" label="RG" outlined dense hide-details />
          </v-col>
        </v-row>

        <h3 class="font-weight-regular mt-10 mb-4">
          <v-icon left>mdi-map-marker-radius-outline</v-icon>
          Endereço
        </h3>
        <v-row>
          <v-col cols="12" md="4">
            <app-cep-field v-model="form.main_address.postal_code" @address="setAddress($event)" outlined dense
              hide-details />
          </v-col>
          <v-col cols="12" md="8">
            <app-text-field v-model="form.main_address.address" label="Endereço" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.main_address.number" label="Número" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.main_address.district" label="Bairro" outlined dense hide-details />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="8" md="6">
            <app-text-field v-model="form.main_address.city" label="Cidade" outlined dense hide-details />
          </v-col>
          <v-col cols="4" md="6">
            <app-text-field v-model="form.main_address.state" label="Estado" outlined dense hide-details />
          </v-col>
          <v-col cols="12">
            <app-text-field v-model="form.main_address.complement" label="Complemento" outlined dense hide-details />
          </v-col>
        </v-row>

        <h3 class="font-weight-regular mt-10 mb-4">
          <v-icon left>mdi-account-cash-outline</v-icon>
          Responsável Financeiro
        </h3>
        <v-row>
          <v-col cols="12">
            <app-text-field v-model="form.financial_responsible_name" label="Nome" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-whatsapp-field v-model="form.financial_responsible_whatsapp" label="WhatsApp" outlined dense
              hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.financial_responsible_email" label="E-mail" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-cpf-field v-model="form.financial_responsible_cpf" label="CPF" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.financial_responsible_rg" label="RG" outlined dense hide-details />
          </v-col>
        </v-row>

        <h3 class="font-weight-regular mt-10 mb-4">
          <v-icon left>mdi-account-alert-outline</v-icon>
          Contato de emergência
        </h3>
        <v-row>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.emergency_contact_name" label="Nome" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-whatsapp-field v-model="form.emergency_contact_whatsapp" label="WhatsApp" outlined dense
              hide-details />
          </v-col>
        </v-row>

        <h3 class="font-weight-regular mt-10 mb-4">
          <v-icon left>mdi-clipboard-edit-outline</v-icon>
          Dados Adicionais
        </h3>
        <v-row>
          <v-col cols="5" md="6">
            <app-select v-model="form.genre" label="Gênero" :items="genreOptions" outlined dense hide-details />
          </v-col>
          <v-col cols="7" md="6">
            <app-date-picker v-model="form.birth_date" label="Data de Nascimento" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.nationality" label="Naturalidade" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.scholarity" label="Escolaridade" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.occupation" label="Profissão" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.forwarding" label="Encaminhamento" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="6">
            <app-text-field v-model="form.civil_status" label="Estado Civil" outlined dense hide-details />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea v-model="form.details" label="Queixa inicial e informações adicionais..." auto-grow outlined
              dense hide-details flat />
          </v-col>
        </v-row>
      </v-form>

      <v-row class="mt-4">
        <v-col>
          <v-btn @click="validationForm()" block color="primary" :loading="sending">
            Enviar cadastro
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
export default {
  data: () => ({
    loading: true,
    linkInvalid: false,
    sending: false,
    sent: false,
    valid: true,

    company: {},
    professional: {},

    requiredRules: [(v) => !!v || "Campo obrigatório"],

    genreOptions: [
      { value: "female", text: "Feminino" },
      { value: "male", text: "Masculino" },
      { value: "others", text: "Outros" },
    ],

    form: {
      name: null,
      phone_wa: null,
      email: null,
      cpf: null,
      rg: null,
      birth_date: null,

      main_address: {
        postal_code: null,
        address: null,
        number: null,
        district: null,
        city: null,
        state: null,
        complement: null,
      },

      financial_responsible_name: null,
      financial_responsible_whatsapp: null,
      financial_responsible_email: null,
      financial_responsible_cpf: null,
      financial_responsible_rg: null,

      emergency_contact_name: null,
      emergency_contact_whatsapp: null,

      genre: null,
      nationality: null,
      scholarity: null,
      occupation: null,
      forwarding: null,
      civil_status: null,
      details: null,
    },
  }),

  computed: {
    customerAlias() {
      return "paciente";
    },
  },

  created() {
    this.getRegistrationData();
  },

  methods: {
    getRegistrationData() {
      this.$http
        .show("app/cadastro-paciente", this.$route.params.token)
        .then((response) => {
          this.company = response.company;
          this.professional = response.professional;
          this.loading = false;
        })
        .catch(() => {
          this.linkInvalid = true;
          this.loading = false;
        });
    },

    setAddress(address) {
      this.form.main_address = { ...this.form.main_address, ...address };
    },

    validationForm() {
      if (this.$refs.form.validate()) {
        this.sendRegistration();
      }
    },

    sendRegistration() {
      this.sending = true;

      this.$http
        .update("app/cadastro-paciente", this.$route.params.token, this.form)
        .then(() => {
          this.sending = false;
          this.sent = true;
        })
        .catch(() => {
          this.sending = false;
        });
    },
  },
};
</script>

<style></style>
